import {
  IonBackButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonChip,
  IonContent,
  IonGrid,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
  IonButton,
  IonToast,
  IonAlert,
  IonDatetime,
} from "@ionic/react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getPlotSensorData, reCalibrateDeviceById } from "../services/plot.service";
import TimeSeriesChart from "./TimeSeriesChart";
import TimeRangeSelector from "./TimeRangeSelector";
import { TimeRangeSelection } from "../utilities/timeRange";
import { createFieldExtractor } from "../utilities/utils";
import { sendCalibrationData, getSIMDetails, activateSIM, suspendSIM } from "../services/device.service";
import { handleErrors } from "../services/common.service";
import _ from "lodash";
import useUserDataStore from "../store/userDataStore";

interface IProps {
  plot?: any;
  sinceHours?: string;
}

export function getChartData(data: any[], field: string) {
  return data.map((item: any, index: number) => {
    return {
      time: item.timestamp,
      value: item[field],
      anomalies: item.anomalies ? item.anomalies[field] : null,
    };
  });
}

type CalibrationData = {
  [key: string]: { [field: string]: string | number };
};

const PlotSensorData: React.FC<IProps> = (props) => {
  const location = useLocation();
  const [plot, setPlot] = useState<any>();
  const [timeRange, setTimeRange] = useState<TimeRangeSelection>({ mode: "sinceHours", sinceHours: 24 });
  const [version, setVersion] = useState<number>();
  const [device, setDevice] = useState<any>();
  const [anomalousFields, setAnomalousFields] = useState<string[]>([]);
  const [sensorData, setSensorData] = useState<any[]>([]);
  const [nearbyPlots, setNearbyPlots] = useState<string[]>([]);
  const [selectedNearbyPlot, setSelectedNearbyPlot] = useState<string | null>();
  const [additionalData, setAdditionalData] = useState<any[]>([]);
  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");
  const [showSIMAlert, setShowSIMAlert] = useState(false);
  const [simStatus, setSimStatus] = useState<string>('');
  const [simDetails, setSimDetails] = useState<Record<string, any> | null>(null);
  const [isLoadingSIM, setIsLoadingSIM] = useState(false);
  const [recalibrateDate, setRecalibrateDate] = useState<string>("");
  const [isRecalibrating, setIsRecalibrating] = useState(false);

  const { isPrivilegeUser } = useUserDataStore();

  useEffect(() => {
    setPlot((location.state as any)?.plot ?? null);
    setTimeRange({
      mode: "sinceHours",
      sinceHours: (location.state as any)?.sinceHours ?? 24,
    });
  }, [location.state]);

  useEffect(() => {
    let mounted = true;
    if (plot && timeRange) {
      getPlotSensorData(plot.plotId, timeRange)
        .then((sensorData) => {
          if (mounted) {
            setAnomalousFields(sensorData.anomalousFields || []);
            setSensorData(sensorData.data || []);
            setDevice(sensorData.device);
            setNearbyPlots(plot.nearestPlots || []);
            const latestData =
              sensorData.data && sensorData.data[sensorData.data.length - 1];
            setVersion(latestData?.Vp || latestData?.Vi);
          }
        })
        .catch((err: any) => {
          if (mounted) {
            setSensorData([]);
            setAnomalousFields([]);
            setOnError(err?.message || "Failed to fetch sensor data");
          }
        });
    }
    return () => {
      mounted = false;
    };
  }, [plot, timeRange]);

  function selectNearbyPlot(plotId: string) {
    if (plotId === selectedNearbyPlot) {
      setSelectedNearbyPlot(null);
      setAdditionalData([]);
    } else {
      setSelectedNearbyPlot(plotId);
      getPlotSensorData(plotId, timeRange)
        .then((sensorData) => {
          setAdditionalData(sensorData.data);
        })
        .catch((err: any) => {
          setAdditionalData([]);
          setOnError(err?.message || "Failed to fetch sensor data");
        });
    }
  }

  const handleCheckSIMStatus = async () => {
    if (!device?.imei) {
      setOnError('Device IMEI not found');
      return;
    }
    setIsLoadingSIM(true);
    try {
      const response = await getSIMDetails(device.imei);
      const data = await response.json();
      setSimDetails(data);
      const rawStatus = data?.status || data?.state || null;
      const normalizedStatus = rawStatus === 'Suspend' ? 'Suspended' : rawStatus;
      setSimStatus(normalizedStatus);
      setShowSIMAlert(true);
    } catch (error) {
      setOnError('Failed to get SIM status');
    } finally {
      setIsLoadingSIM(false);
    }
  };

  const handleSIMAction = async (action: 'activate' | 'suspend') => {
    if (!device?.imei) {
      setOnError('Device IMEI not found');
      return;
    }

    try {
      const response = action === 'activate'
        ? await activateSIM(device.imei)
        : await suspendSIM(device.imei);

      const payload = await response.json();

      if (payload?.status === 'failed') {
        const errorMsg = Object.values(payload)
          .filter((v): v is Record<string, any> => typeof v === 'object' && v !== null)
          .map((v) => v.ErrorMessage)
          .find(Boolean);
        setOnError(errorMsg || `Failed to ${action} SIM`);
        return;
      }

      setSimStatus(action === 'activate' ? 'Active' : 'Suspended');
      setOnSuccess(`SIM ${action}d successfully`);
      setShowSIMAlert(false);
    } catch (error) {
      setOnError(`Error ${action}ing SIM`);
    }
  };

  const handleRecalibrate = async () => {
    if (!recalibrateDate) {
      setOnError('Please select a start date');
      return;
    }

    if (!plot?.plotId) {
      setOnError('Plot ID not found');
      return;
    }

    setIsRecalibrating(true);
    try {
      // Format date as YYYY-MM-DD
      const formattedDate = new Date(recalibrateDate).toISOString().split('T')[0];
      await reCalibrateDeviceById(plot.plotId, formattedDate).then(handleErrors);
      setOnSuccess('Device re-calibrated successfully');
      setRecalibrateDate('');
    } catch (err: any) {
      setOnError(err?.message || 'Failed to recalibrate device');
    } finally {
      setIsRecalibrating(false);
    }
  };

  const extractNumericFields = createFieldExtractor("number");
  const priorityFields = ['I1', 'I2', 'moisture1', 'soilMoisture1', 'moisture2', 'soilMoisture2'];
  const allFields = extractNumericFields(sensorData ?? []);
  const fields = [
    ...priorityFields.filter((f) => allFields.includes(f)),
    ...allFields.filter((f) => !priorityFields.includes(f)),
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Sensor Data</IonTitle>
        </IonToolbar>
      </IonHeader>
      {!plot && (
        <IonContent>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60%', gap: '8px', padding: '24px' }}>
            <IonLabel color="medium" style={{ fontSize: '15px', textAlign: 'center' }}>
              No plot selected. Please navigate here from a plot card.
            </IonLabel>
          </div>
        </IonContent>
      )}
      {plot && (
        <IonContent>
          <IonList>
            <IonCard style={{ margin: '16px', borderRadius: '12px' }}>
              <IonItem lines="none" style={{ '--background': 'transparent' }}>
                <IonLabel>
                  <h2 style={{ fontWeight: 'bold', fontSize: '18px', margin: '0 0 8px 0' }}>
                    {plot.name || 'Unnamed Plot'}
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Plot ID:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{plot.plotId}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Batch ID:</span>
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{device?.batchId || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>
                        Firmware version
                        {sensorData[sensorData.length - 1]?.Vp
                          ? " (Vp)"
                          : sensorData[sensorData.length - 1]?.Vi
                            ? " (Vi)"
                            : ""}:
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>{version || 'N/A'}</span>
                    </div>
                  </div>
                </IonLabel>
              </IonItem>
            </IonCard>



            {device?.calibration && device?.sensors && (
              <IonCard style={{ margin: '16px', borderRadius: '12px' }}>
                <IonItem lines="none" style={{ '--background': 'transparent' }}>
                  <IonLabel>
                    <h2 style={{ fontWeight: 'bold', fontSize: '18px', margin: '0 0 8px 0' }}>
                      Calibration
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {device.sensors
                        .filter((sensor: string) => sensor.startsWith('moisture'))
                        .map((sensor: string) => {
                          const calibration = device.calibration[sensor];
                          return (
                            <div key={sensor} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}>
                              <span style={{ fontSize: '14px', color: '#666' }}>{sensor}:</span>
                              <span style={{
                                fontSize: '14px',
                                color: calibration?.soilMax ? '#3880ff' : '#666',
                                fontWeight: calibration?.soilMax ? '600' : '400'
                              }}>
                                {calibration?.soilMax ? `${calibration.soilMax}` : 'Not set'}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </IonLabel>
                </IonItem>
              </IonCard>
            )}

            <div style={{ display: 'flex', gap: '8px', padding: '0 16px 16px 16px' }}>

              {device?.imei && (
                <IonButton
                  size="small"
                  color="primary"
                  onClick={handleCheckSIMStatus}
                  disabled={isLoadingSIM}
                  style={{ flex: 1 }}
                >
                  {isLoadingSIM ? 'Checking...' : 'Check SIM Status'}
                </IonButton>
              )}
            </div>

            {isPrivilegeUser && (
              <>
                <IonItem style={{ margin: '0 16px 8px 16px', borderRadius: '8px' }}>
                  <IonLabel position="stacked">Start Date</IonLabel>
                  <IonDatetime
                    id="recalibrate-date"
                    name="recalibrate-date"
                    displayFormat="DD/MM/YYYY"
                    placeholder="Select Start Date"
                    value={recalibrateDate}
                    onIonChange={(e) => setRecalibrateDate(e.detail.value!)}
                  />
                </IonItem>

                <div style={{ padding: '0 16px 16px 16px' }}>
                  <IonButton
                    expand="block"
                    color="primary"
                    onClick={handleRecalibrate}
                    disabled={isRecalibrating || !recalibrateDate}
                  >
                    {isRecalibrating ? 'Recalibrating...' : 'ReCalibrate Device'}
                  </IonButton>
                </div>
              </>
            )}

            {anomalousFields?.length > 0 && (
              <>
                <IonItem lines="none">
                  <IonLabel>Anomalous Fields</IonLabel>
                </IonItem>
                <IonItem lines="none">
                  <IonGrid>
                    <IonRow>
                      {anomalousFields.map((key: any, index: number) => (
                        <IonChip outline={true} color="primary" key={index}>
                          <IonLabel>{key}</IonLabel>
                        </IonChip>
                      ))}
                    </IonRow>
                  </IonGrid>
                </IonItem>
              </>
            )}

            {nearbyPlots?.length > 0 && (
              <>
                <IonItem lines="none">
                  <IonLabel>Nearby Plots</IonLabel>
                </IonItem>
                <IonItem lines="none">
                  <IonGrid>
                    <IonRow>
                      {nearbyPlots.map((key: any, index: number) => (
                        <IonChip
                          outline={key === selectedNearbyPlot ? false : true}
                          color="primary"
                          key={index}
                          onClick={(e) => selectNearbyPlot(key)}
                        >
                          <IonLabel>{key}</IonLabel>
                        </IonChip>
                      ))}
                    </IonRow>
                  </IonGrid>
                </IonItem>
              </>
            )}

            <TimeRangeSelector
              value={timeRange}
              onApply={setTimeRange}
              defaultSinceHours={24}
              idPrefix="sensor-data"
            />

            {fields?.map((field: string, index: number) => {
              return (
                <IonCard id={field} key={index}>
                  <IonCardHeader>
                    <IonCardSubtitle>{field}</IonCardSubtitle>
                  </IonCardHeader>
                  <TimeSeriesChart
                    format={"date"}
                    chartData={getChartData(sensorData, field)}
                    additionalData={getChartData(additionalData, field)}
                  />
                </IonCard>
              );
            })}
          </IonList>
        </IonContent>
      )}

      <IonAlert
        isOpen={showSIMAlert}
        onDidDismiss={() => setShowSIMAlert(false)}
        header="SIM Status"
        message={[
          `Current SIM status: ${simStatus}`,
          simDetails?.provider ? `Provider: ${simDetails.provider.toUpperCase()}` : null,
          simDetails?.iccid ? `ICCID: ${simDetails.iccid}` : null,
          simDetails?.msisdn ? `MSISDN: ${simDetails.msisdn}` : null,
        ].filter(Boolean).join('<br>')}
        buttons={[
          {
            text: 'Close',
            role: 'cancel',
            cssClass: 'secondary'
          },
          ...(simStatus === 'Active' ? [{
            text: 'Suspend',
            handler: () => handleSIMAction('suspend')
          }] : []),
          ...(simStatus === 'Suspended' ? [{
            text: 'Activate',
            handler: () => handleSIMAction('activate')
          }] : [])
        ]}
      />

      <IonToast
        isOpen={!!onSuccess}
        onDidDismiss={() => setOnSuccess("")}
        message={onSuccess}
        duration={2000}
        color="success"
      />

      <IonToast
        isOpen={!!onError}
        onDidDismiss={() => setOnError("")}
        message={onError}
        duration={2000}
        color="danger"
      />
    </IonPage>
  );
};

export default PlotSensorData;
